import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Trophy, RotateCcw, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { alpha } from "@/lib/color";
import { confettiColors, accentColor, accentBackground } from "@/lib/gameStyle";
import ShareBoost from "@/components/ShareBoost";

function Confetti({ game }) {
  const colors = confettiColors(game);
  return (
    <div aria-hidden="true" className="absolute inset-x-0 top-0 h-56 overflow-hidden pointer-events-none">
      {[...Array(22)].map((_, i) => (
        <span
          key={i}
          className="absolute top-0 block rounded-sm"
          style={{
            left: `${(i * 4.7) % 100}%`,
            width: i % 3 === 0 ? 8 : 5,
            height: i % 3 === 0 ? 5 : 9,
            background: colors[i % colors.length],
            "--qrise-x": `${(i % 5) * 6 - 12}px`,
            animation: `qrise-confetti ${2.4 + ((i * 0.3) % 1.6)}s ${(i % 7) * 0.18}s cubic-bezier(.3,.2,.2,1) infinite`,
          }}
        />
      ))}
    </div>
  );
}

/** Result screen: perfect score → entered in the draw; otherwise score + wrong questions + last-chance retry. */
export default function QuizResult({ game, result, questions, onRetry }) {
  const { t } = useTranslation();
  const theme = accentColor(game);
  const themeBg = accentBackground(game);
  const wrong = new Set(result.wrong_indexes || []);

  if (result.all_correct) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        className="qrise-card relative overflow-hidden p-7 text-center"
        data-testid="quiz-result-success"
      >
        <Confetti game={game} />
        <div
          className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full"
          style={{ background: alpha(theme, 0.15), color: theme }}
        >
          <Trophy className="h-10 w-10" />
        </div>
        <div className="text-[11px] font-bold uppercase tracking-[0.28em]" style={{ color: theme }}>
          {t("play.quiz_perfect_kicker")}
        </div>
        <h3 className="mt-1 text-2xl font-bold tracking-tight" data-testid="quiz-result-score">
          {t("play.quiz_score", { score: result.score, total: result.total })}
        </h3>
        <div className="mt-5 text-xl font-semibold tracking-tight" data-testid="quiz-confirmed-title">
          {game.confirm_title?.trim() || t("play.lottery_confirmed_title")}
        </div>
        <p
          className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-md mx-auto"
          data-testid="quiz-draw-notice"
        >
          {game.confirm_body?.trim() || t("play.quiz_confirmed_body", { count: result.winners_count || 1 })}
        </p>
        <ShareBoost
          game={game}
          sessionId={result.session_id}
          shareText={t("play.quiz_share_text", { title: game.title || "QRISE" })}
          testIdPrefix="quiz"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="qrise-card p-6 sm:p-7 space-y-5"
      data-testid="quiz-result-fail"
    >
      <div className="text-center">
        <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
          {t("play.quiz_fail_kicker")}
        </div>
        <h3 className="mt-1 text-3xl font-bold tracking-tight" data-testid="quiz-result-score">
          {t("play.quiz_score", { score: result.score, total: result.total })}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {result.can_retry
            ? game.quiz_retry_message?.trim() ||
              t(game.quiz_unlimited_plays ? "play.quiz_fail_retry_body_unlimited" : "play.quiz_fail_retry_body")
            : t("play.quiz_fail_final_body")}
        </p>
      </div>

      <ul className="space-y-2" data-testid="quiz-review-list">
        {questions.map((q, i) => {
          const bad = wrong.has(i);
          return (
            <li
              key={i}
              className="flex items-start gap-3 rounded-xl border px-3.5 py-2.5 text-sm"
              style={{
                borderColor: bad ? "rgba(239,68,68,0.45)" : "rgba(16,185,129,0.35)",
                background: bad ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.06)",
              }}
              data-testid={`quiz-review-${i}`}
              data-correct={!bad}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white ${bad ? "bg-red-500" : "bg-emerald-500"}`}
              >
                {bad ? <X className="h-3 w-3" strokeWidth={3} /> : <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span className={bad ? "font-semibold" : "text-muted-foreground"}>
                <span className="mr-1.5 text-[11px] font-bold uppercase tracking-wider opacity-70">Q{i + 1}</span>
                {q.text}
              </span>
            </li>
          );
        })}
      </ul>

      {result.can_retry ? (
        <Button
          type="button"
          onClick={onRetry}
          className="w-full h-auto min-h-12 py-3 px-4 rounded-xl whitespace-normal hover:opacity-90"
          style={{ background: themeBg, color: game.button_text_color || "#fff" }}
          data-testid="quiz-retry-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2 shrink-0" />
          <span className="flex flex-col items-start leading-tight text-left">
            <span className="text-sm font-bold uppercase tracking-wider">{t("play.quiz_retry_btn")}</span>
            <span className="text-[11px] font-medium normal-case tracking-normal opacity-85">
              {t(game.quiz_unlimited_plays ? "play.quiz_retry_sub_unlimited" : "play.quiz_retry_sub")}
            </span>
          </span>
        </Button>
      ) : (
        <p className="text-center text-xs text-muted-foreground" data-testid="quiz-no-retry">
          {t("play.quiz_no_attempts")}
        </p>
      )}
    </motion.div>
  );
}
